import { BigNumber, BigNumberish } from 'ethers'
import React from 'react'
import { TransactionState } from '../../../../../web3/hooks/types'
import { useWeb3Connection } from '../../../../../web3/Web3Provider'
import { TokenDetails } from '../balance/BalanceProvider'
import { ActionLogItem } from '../useFundDirectPayment'
import { useAllowance } from './useAllowance'
import { TokenData, useTokenData } from './useTokenData'

interface IAllowanceContext {
    allowanceLoading: boolean
    approvalLoading: boolean
    hasAllowance: boolean
    currentAllowance?: BigNumber
    approve: (amount?: BigNumberish) => Promise<void>
    deny: () => Promise<void>
    actionLog: ActionLogItem | null
    approveStatus: TransactionState
    refreshAllowance: () => void
    clearActionLog: () => void
    tokenData: TokenData
}

export const AllowanceContext = React.createContext<IAllowanceContext>({} as IAllowanceContext)

export const useAllowanceContext = () => {
    const context = React.useContext(AllowanceContext)
    if (!context)
        throw new Error('Cannot use useAllowanceContext outside of a <AllowanceProvider />')
    return context
}

export const AllowanceProvider: React.FC<{ token?: TokenDetails }> = ({ token, children }) => {
    const { connectedNetwork } = useWeb3Connection()
    const { tokenData } = useTokenData(token?.address, connectedNetwork)

    const {
        allowanceLoading,
        currentAllowance,
        hasAllowance,
        approve,
        approvalLoading,
        actionLog,
        approveStatus,
        refreshAllowance,
        deny,
        clearActionLog,
    } = useAllowance(token)

    return (
        <AllowanceContext.Provider
            value={{
                allowanceLoading,
                currentAllowance,
                hasAllowance,
                approve,
                approvalLoading,
                actionLog,
                approveStatus,
                refreshAllowance,
                deny,
                clearActionLog,
                tokenData,
            }}
        >
            {children}
        </AllowanceContext.Provider>
    )
}
